import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Search, X, Star, TrendingUp, User, Calendar } from 'lucide-react';
import AttachmentViewer from '../common/AttachmentViewer'
import { formatCurrency } from '../../config/currency';
import './PerformanceModule.css';

const API_URL = import.meta.env.VITE_API_URL || 'https://www.secs.gismac.co.ke/api';

const PerformanceModule = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    personnelId: '',
    reviewPeriod: '',
    reviewer: '',
    attendance: 3,
    punctuality: 3,
    jobKnowledge: 3,
    productivity: 3,
    communication: 3,
    teamwork: 3,
    initiative: 3,
    discipline: 3,
    strengths: '',
    areasForImprovement: '',
    goals: '',
    reviewerComments: '',
    appraisalStatus: 'Pending',
    recommendedAction: 'None'
  });

  useEffect(() => {
    fetchRecords();
  }, []);
        // Real-time overall score calculation
  useEffect(() => {
    const scores = [
      formData.attendance || 3,
      formData.punctuality || 3,
      formData.jobKnowledge || 3,
      formData.productivity || 3,
      formData.communication || 3,
      formData.teamwork || 3,
      formData.initiative || 3,
      formData.discipline || 3
    ];
    const overall = parseFloat((scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2));
    setFormData(prev => ({ ...prev, overallScore: overall }));
  }, [formData.attendance, formData.punctuality, formData.jobKnowledge, formData.productivity, formData.communication, formData.teamwork, formData.initiative, formData.discipline]); 
                   
 

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/performance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecords(res.data.data || []);
    } catch (err) {
      console.error('Error fetching performance records:', err);
    } finally {
      setLoading(false);
    }
  };

    const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      const data = new FormData();
      data.append('personnelId', formData.personnelId || '');
      data.append('reviewPeriod', formData.reviewPeriod || '');
      data.append('reviewer', formData.reviewer || '');
      data.append('attendance', formData.attendance || 3);
      data.append('punctuality', formData.punctuality || 3);
      data.append('jobKnowledge', formData.jobKnowledge || 3);
      data.append('productivity', formData.productivity || 3);
      data.append('communication', formData.communication || 3);
      data.append('teamwork', formData.teamwork || 3);
      data.append('initiative', formData.initiative || 3);
      data.append('discipline', formData.discipline || 3);
      data.append('strengths', formData.strengths || '');
      data.append('areasForImprovement', formData.areasForImprovement || '');
      data.append('goals', formData.goals || '');
      data.append('reviewerComments', formData.reviewerComments || '');
      data.append('appraisalStatus', formData.appraisalStatus || 'Pending');
      data.append('recommendedAction', formData.recommendedAction || 'None');
      
      // Attachment MUST be last for multer to parse text fields first
           if (formData.attachments && formData.attachments.length > 0) {
        formData.attachments.forEach(file => data.append('attachments', file));
      }
      
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      
      if (selectedRecord) {
        await axios.put(`${API_URL}/performance/${selectedRecord._id}`, data, config);
      } else {
        await axios.post(`${API_URL}/performance`, data, config);
      }
      setShowModal(false);
      setSelectedRecord(null);
      resetForm();
      fetchRecords();
    } catch (err) {
      console.error('Error saving performance record:', err);
      alert(err.response?.data?.message || 'Failed to save record');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this performance record?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/performance/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRecords();
    } catch (err) {
      console.error('Error deleting record:', err);
      alert('Failed to delete record');
    }
  };

  const resetForm = () => {
    setFormData({
      personnelId: '',
      reviewPeriod: '',
      reviewer: '',
      attendance: 3,
      punctuality: 3,
      jobKnowledge: 3,
      productivity: 3,
      communication: 3,
      teamwork: 3,
      initiative: 3,
      discipline: 3,
      strengths: '',
      areasForImprovement: '',
      goals: '',
      reviewerComments: '',
      appraisalStatus: 'Pending',
      recommendedAction: 'None'
    });
  };

  const openModal = (record = null) => {
    if (record) {
      setSelectedRecord(record);
      setFormData({
        personnelId: record.personnelId?._id || record.personnelId,
        reviewPeriod: record.reviewPeriod,
        reviewer: record.reviewer,
        attendance: record.attendance,
        punctuality: record.punctuality,
        jobKnowledge: record.jobKnowledge,
        productivity: record.productivity,
        communication: record.communication,
        teamwork: record.teamwork,
        initiative: record.initiative,
        discipline: record.discipline,
        strengths: record.strengths || '',
        areasForImprovement: record.areasForImprovement || '',
        goals: record.goals || '',
        reviewerComments: record.reviewerComments || '',
        appraisalStatus: record.appraisalStatus,
        recommendedAction: record.recommendedAction
      });
    } else {
      setSelectedRecord(null);
      resetForm();
    }
    setShowModal(true);
  };

  const getScoreColor = (score) => {
    if (score >= 4.5) return '#4caf50';
    if (score >= 3.5) return '#8bc34a';
    if (score >= 2.5) return '#ff9800';
    if (score >= 1.5) return '#ff5722';
    return '#f44336';
  };

  const filteredRecords = records.filter(r => 
    String(r.personnelId || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(r.personnelId || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.reviewPeriod?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.reviewer?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="performance-module">
      <div className="section-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search performance records..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <Plus size={18} />
          Add Performance Review
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Personnel</th>
                <th>Review Period</th>
                <th>Reviewer</th>
                <th>Overall Score</th>
                <th>Status</th>
                <th>Recommended Action</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">No performance records found</td>
                </tr>
              ) : (
                filteredRecords.map(record => (
                  <tr key={record._id}>
                                        <td>
                      <div className="personnel-info">
                        <strong>{record.personnelId || "N/A"}</strong>
                        <span className="text-muted">Personnel</span>
                      </div>
                                            {record.attachments && record.attachments.map((url, idx) => (
                        <a key={idx} href={url} target="_blank">File {idx + 1}</a>
                      ))}
                    </td>
                    <td>{record.reviewPeriod}</td>
                    <td>{record.reviewer}</td>
                    <td>
                      <span className="score-badge" style={{ backgroundColor: getScoreColor(record.overallScore) }}>
                        {record.overallScore ? parseFloat(record.overallScore).toFixed(1) : '0.0'} / 5
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${record.appraisalStatus.toLowerCase()}`}>
                        {record.appraisalStatus}
                      </span>
                    </td>
                    <td>{record.recommendedAction}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon" onClick={() => openModal(record)} title="Edit">
                          <TrendingUp size={16} />
                        </button>
                        <button className="btn-icon btn-danger" onClick={() => handleDelete(record._id)} title="Delete">
                          <X size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-content modal-lg">
            <div className="modal-header">
              <h3>{selectedRecord ? 'Edit' : 'Add'} Performance Review</h3>
              <button onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                                  <div className="form-group"><label>Personnel Name *</label><input type="text" value={formData.personnelId} onChange={(e) => setFormData({...formData, personnelId: e.target.value})} required placeholder="Enter personnel ID or name" /></div>
                <div className="form-group">
                  <label>Review Period *</label>
                  <input 
                    type="text" 
                    value={formData.reviewPeriod}
                    onChange={(e) => setFormData({...formData, reviewPeriod: e.target.value})}
                    required
                    placeholder="e.g., Q1 2026"
                  />
                </div>
                <div className="form-group">
                  <label>Reviewer *</label>
                  <input 
                    type="text" 
                    value={formData.reviewer}
                    onChange={(e) => setFormData({...formData, reviewer: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Appraisal Status</label>
                  <select 
                    value={formData.appraisalStatus}
                    onChange={(e) => setFormData({...formData, appraisalStatus: e.target.value})}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Reviewed">Reviewed</option>
                    <option value="ongoing">Ongoing</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
              </div>
             <div className="form-group">
  <label>Attachment (Document)</label>
   <input 
    type="file" 
    multiple
    name="attachments" 
    onChange={(e) => setFormData(prev => ({ ...prev, attachments: Array.from(e.target.files) }))}
  />
                                    {selectedRecord && selectedRecord.attachments && selectedRecord.attachments.map((url, idx) => (
                    <div key={idx} className="form-group ca-form-full">
                      <label>Current File {idx + 1}</label>
                      <a href={url} target="_blank">View</a>
                    </div>
                  ))}
</div>

              <h4 className="section-title">Performance Metrics (1-5)</h4>
              <div className="metrics-grid">
                {[
                  { key: 'attendance', label: 'Attendance' },
                  { key: 'punctuality', label: 'Punctuality' },
                  { key: 'jobKnowledge', label: 'Job Knowledge' },
                  { key: 'productivity', label: 'Productivity' },
                  { key: 'communication', label: 'Communication' },
                  { key: 'teamwork', label: 'Teamwork' },
                  { key: 'initiative', label: 'Initiative' },
                  { key: 'discipline', label: 'Gismac Culture' }
                ].map(metric => (
                  <div className="form-group" key={metric.key}>
                    <label>{metric.label}</label>
                    <input 
                      type="number" 
                      min="1" 
                      max="5" 
                      value={formData[metric.key]}
                      onChange={(e) => setFormData({...formData, [metric.key]: parseInt(e.target.value)})}
                    />
                  </div>
                ))}
              </div>

              
              {/* Real-time Overall Score Display */}
              <div className="overall-score-card" style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px',
                padding: '20px',
                margin: '20px 0',
                textAlign: 'center',
                color: 'white',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
              }}>
                <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Overall Performance Score</div>
                <div style={{ 
                  fontSize: '48px', 
                  fontWeight: 'bold', 
                  color: getScoreColor(formData.overallScore || 3),
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}>
                  {formData.overallScore ? formData.overallScore.toFixed(1) : '3.0'}
                </div>
                <div style={{ fontSize: '16px', opacity: 0.8, marginTop: '4px' }}>/ 5.00</div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginTop: '12px' }}>
                  {[1,2,3,4,5].map(star => (
                    <Star key={star} size={24} 
                      fill={star <= Math.round(formData.overallScore || 3) ? getScoreColor(formData.overallScore || 3) : 'none'} 
                      color={star <= Math.round(formData.overallScore || 3) ? getScoreColor(formData.overallScore || 3) : 'rgba(255,255,255,0.3)'} 
                    />
                  ))}
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  marginTop: '8px', 
                  padding: '4px 12px', 
                  background: 'rgba(255,255,255,0.2)', 
                  borderRadius: '20px',
                  display: 'inline-block'
                }}>
                  {formData.overallScore >= 4.5 ? 'Outstanding' : 
                   formData.overallScore >= 3.5 ? 'Good' : 
                   formData.overallScore >= 2.5 ? 'Average' : 
                   formData.overallScore >= 1.5 ? 'Below Average' : 'Poor'}
                </div>
              </div>

n              <div className="form-grid">
                <div className="form-group form-group-full">
                  <label>Strengths</label>
                  <textarea 
                    rows="3"
                    value={formData.strengths}
                    onChange={(e) => setFormData({...formData, strengths: e.target.value})}
                  />
                </div>
                <div className="form-group form-group-full">
                  <label>Areas for Improvement</label>
                  <textarea 
                    rows="3"
                    value={formData.areasForImprovement}
                    onChange={(e) => setFormData({...formData, areasForImprovement: e.target.value})}
                  />
                </div>
                <div className="form-group form-group-full">
                  <label>Goals</label>
                  <textarea 
                    rows="3"
                    value={formData.goals}
                    onChange={(e) => setFormData({...formData, goals: e.target.value})}
                  />
                </div>
                <div className="form-group form-group-full">
                  <label>Reviewer Comments</label>
                  <textarea 
                    rows="3"
                    value={formData.reviewerComments}
                    onChange={(e) => setFormData({...formData, reviewerComments: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Recommended Action</label>
                  <select 
                    value={formData.recommendedAction}
                    onChange={(e) => setFormData({...formData, recommendedAction: e.target.value})}
                  >
                    <option value="None">None</option>
                    <option value="Promotion">Promotion</option>
                    <option value="Salary Increment">Salary Increment</option>
                    <option value="Training">Training</option>
                    <option value="Probation">Probation</option>
                    <option value="Termination">Termination</option>
                    <option value="Demotion">Demotion</option>
                  </select>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">
                  {selectedRecord ? 'Update' : 'Save'} Record
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceModule;
